// Tolerant JSON parser for DB-stored feedback blobs that sometimes contain
// CSV-style doubled quotes (""), stray unescaped quotes, raw control chars,
// lone backslashes, or trailing commas. Returns null if the value cannot be
// recovered, so callers can fall back gracefully instead of crashing.
export const safeJsonParse = (raw: any): any => {
  if (raw == null || typeof raw !== "string") return null;

  const tryParse = (s: string) => {
    try {
      return { ok: true, value: JSON.parse(s) };
    } catch {
      return { ok: false, value: null };
    }
  };

  // 1. fast path — clean JSON
  let r = tryParse(raw);
  if (r.ok) return r.value;

  // 2. trim BOM / whitespace / one layer of wrapping quotes
  let s = raw.replace(/^﻿/, "").trim();
  if (
    s.length >= 2 &&
    s.startsWith('"') &&
    s.endsWith('"') &&
    !s.startsWith('"{') &&
    !s.startsWith('"[')
  ) {
    s = s.slice(1, -1);
  }

  // 3. context-aware repair — only mutate chars inside string literals
  const repair = (input: string) => {
    let out = "";
    let inStr = false;
    for (let k = 0; k < input.length; k++) {
      const c = input[k];
      const next = input[k + 1];
      if (!inStr) {
        if (c === '"') inStr = true;
        out += c;
        continue;
      }
      if (c === "\\") {
        if (
          next === '"' ||
          next === "\\" ||
          next === "/" ||
          next === "b" ||
          next === "f" ||
          next === "n" ||
          next === "r" ||
          next === "t" ||
          next === "u"
        ) {
          out += c + next;
          k++;
        } else {
          out += "\\\\";
        }
        continue;
      }
      if (c === '"') {
        if (next === '"') {
          out += '\\"';
          k++;
          continue;
        }
        let m = k + 1;
        while (m < input.length && /\s/.test(input[m])) m++;
        const after = input[m];
        if (
          after === undefined ||
          after === "," ||
          after === "}" ||
          after === "]" ||
          after === ":"
        ) {
          inStr = false;
          out += c;
        } else {
          out += '\\"';
        }
        continue;
      }
      if (c === "\n") { out += "\\n"; continue; }
      if (c === "\r") { out += "\\r"; continue; }
      if (c === "\t") { out += "\\t"; continue; }
      const code = c.charCodeAt(0);
      if (code < 0x20) {
        out += "\\u" + code.toString(16).padStart(4, "0");
        continue;
      }
      out += c;
    }
    return out;
  };

  s = repair(s);

  // 4. drop trailing commas:  ,}  or  ,]
  s = s.replace(/,\s*([}\]])/g, "$1");

  r = tryParse(s);
  if (r.ok) return r.value;

  console.log("safeJsonParse failed - ", raw);
  return null;
};
